/**
 * React Hook for SSE Location Updates
 * Provides real-time device locations with automatic connection management
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  sseLocationService, 
  SSEDeviceLocation, 
  SSEConnectionStatus 
} from '../services/sseLocationService';
import { DeviceLocationStatus } from '@signals/cop-ui/services/copApi';
import { debugLog } from '../utils/debugLog';

interface UseSSELocationsOptions {
  /** Enable SSE connection (default: true) */
  enabled?: boolean;
  /** Filter by source: 'phone' or 'vehicle' */
  sourceFilter?: string;
  /** Filter by specific device ID */
  deviceFilter?: string;
  /** Merge with existing device data (for hybrid mode) */
  mergeWithExisting?: DeviceLocationStatus[];
}

interface UseSSELocationsResult {
  /** Map of device locations keyed by device_id */
  locations: Map<string, SSEDeviceLocation>;
  /** Array of all locations */
  locationsArray: SSEDeviceLocation[];
  /** Connection status */
  status: SSEConnectionStatus;
  /** Whether connected to SSE */
  isConnected: boolean;
  /** Manually reconnect */
  reconnect: () => void;
  /** Disconnect from SSE */
  disconnect: () => void;
  /** Last update timestamp */
  lastUpdate: Date | null;
  /** Count of devices */
  deviceCount: number;
}

/**
 * Hook for subscribing to real-time SSE location updates
 * 
 * @example
 * ```tsx
 * const { locations, isConnected, status } = useSSELocations({
 *   enabled: true,
 *   sourceFilter: 'phone'
 * });
 * 
 * // Use locations in your component
 * locations.forEach(location => {
 *   console.log(`Device ${location.device_id} at ${location.latitude}, ${location.longitude}`);
 * });
 * ```
 */
export function useSSELocations(options: UseSSELocationsOptions = {}): UseSSELocationsResult {
  const { 
    enabled = true, 
    sourceFilter, 
    deviceFilter,
  } = options;

  const [locations, setLocations] = useState<Map<string, SSEDeviceLocation>>(new Map());
  const [status, setStatus] = useState<SSEConnectionStatus>(() => sseLocationService.getStatus());
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  
  // Track if we're mounted
  const mountedRef = useRef(true);

  // Handle incoming location updates
  const handleLocation = useCallback((location: SSEDeviceLocation) => {
    debugLog('sse', '[SSE Hook] handleLocation called:', location.device_id, 'mounted:', mountedRef.current);
    if (!mountedRef.current) return;
    
    setLocations(prev => {
      const newMap = new Map(prev);
      newMap.set(location.device_id, location);
      debugLog('sse', '[SSE Hook] Updated locations map, size:', newMap.size);
      return newMap;
    });
    setLastUpdate(new Date());
  }, []);

  // Handle status updates
  const handleStatus = useCallback((newStatus: SSEConnectionStatus) => {
    if (!mountedRef.current) return;
    setStatus(newStatus);
  }, []);

  // Connect/disconnect based on enabled flag
  useEffect(() => {
    mountedRef.current = true;
    debugLog('sse', '[SSE Hook] useEffect triggered, enabled:', enabled);

    if (enabled) {
      debugLog('sse', '[SSE Hook] Subscribing to SSE updates...');
      // Subscribe to updates
      const unsubLocation = sseLocationService.onLocation(handleLocation);
      const unsubStatus = sseLocationService.onStatus(handleStatus);
      
      // Connect (force reconnect to ensure fresh connection)
      sseLocationService.connect(sourceFilter, deviceFilter, true);
      
      return () => {
        debugLog('sse', '[SSE Hook] Cleanup - unsubscribing');
        mountedRef.current = false;
        unsubLocation();
        unsubStatus();
        // Note: Don't disconnect here - other components might be using the connection
      };
    } else {
      debugLog('sse', '[SSE Hook] Disabled, not connecting');
      return () => {
        mountedRef.current = false;
      };
    }
  }, [enabled, sourceFilter, deviceFilter, handleLocation, handleStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const reconnect = useCallback(() => {
    sseLocationService.disconnect();
    sseLocationService.connect(sourceFilter, deviceFilter);
  }, [sourceFilter, deviceFilter]);

  const disconnect = useCallback(() => {
    sseLocationService.disconnect();
  }, []);

  const locationsArray = Array.from(locations.values());

  return {
    locations,
    locationsArray,
    status,
    isConnected: status.connected,
    reconnect,
    disconnect,
    lastUpdate,
    deviceCount: locations.size
  };
}

/**
 * Convert SSE location to COP DeviceLocationStatus format
 * For compatibility with existing COP components
 */
export function sseToDeviceLocationStatus(sseLocation: SSEDeviceLocation): DeviceLocationStatus {
  // Always calculate locally - backend timestamp parsing has timezone issues
  const lastSeenMinutes = calculateMinutesAgo(sseLocation.timestamp);
  
  return {
    device_id: sseLocation.device_id,
    entity_id: sseLocation.entity_id,
    entity_type: sseLocation.entity_type,
    course_id: sseLocation.course_id,
    latitude: sseLocation.latitude,
    longitude: sseLocation.longitude,
    timestamp: sseLocation.timestamp,
    speed: sseLocation.speed,
    bearing: sseLocation.heading,
    // Use enriched device_type if available, otherwise infer from source
    device_type:
      sseLocation.device_type ||
      (sseLocation.source === 'vehicle'
        ? 'Vehicle'
        : sseLocation.source === 'drone'
          ? 'Drone'
          : 'Phone'),
    last_seen_minutes_ago: lastSeenMinutes,
    // Calculate is_stale locally (15 min threshold)
    is_stale: lastSeenMinutes > 15,
    // Leave undefined when backend omits the field so COP merge can keep prior Live state
    is_streaming:
      sseLocation.is_streaming === undefined || sseLocation.is_streaming === null
        ? undefined
        : Boolean(sseLocation.is_streaming),

    // Aircraft only; undefined for every other device type.
    altitude_agl_m: sseLocation.altitude_agl_m,
    gimbal_pitch_deg: sseLocation.gimbal_pitch_deg,
    gimbal_yaw_deg: sseLocation.gimbal_yaw_deg,
    gimbal_yaw_rel_deg: sseLocation.gimbal_yaw_rel_deg,
    gimbal_roll_deg: sseLocation.gimbal_roll_deg,
    gimbal_mode: sseLocation.gimbal_mode,
    hfov_deg: sseLocation.hfov_deg,
    vfov_deg: sseLocation.vfov_deg,
    is_flying: sseLocation.is_flying,
    vertical_speed_ms: sseLocation.vertical_speed_ms,
    satellite_count: sseLocation.satellite_count,
    home_latitude: sseLocation.home_latitude,
    home_longitude: sseLocation.home_longitude,
    platform: sseLocation.platform,
    session_id: sseLocation.session_id,
  };
}

/**
 * Calculate minutes ago from timestamp
 * Handles format: "2025-12-15 18:28:54" (local time without timezone)
 */
function calculateMinutesAgo(timestamp: string): number {
  if (!timestamp) return 999;
  
  // The MQTT timestamp is in local time format: "YYYY-MM-DD HH:mm:ss"
  // JavaScript Date needs "YYYY-MM-DDTHH:mm:ss" format
  // Don't add 'Z' - treat as local time
  const normalizedTs = timestamp.replace(' ', 'T');
  const time = new Date(normalizedTs).getTime();
  
  if (isNaN(time)) {
    console.warn('[SSE] Failed to parse timestamp:', timestamp);
    return 999;
  }
  
  const now = Date.now();
  const minutes = Math.floor((now - time) / 60000);
  return Math.max(0, minutes); // Don't return negative
}

export default useSSELocations;
